import React from 'react';
import { mapObject, constant, noop } from 'underscore';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import { Col } from '@bingads-webui-react/layout';
import { withDefaultStyles } from '@bingads-webui-react/with-default-styles';
import { Activity, Scenario } from '@bingads-webui-react/hoc-with-signal';
import { getToolbarStyles } from '../../styles';
import {
  ImportBar, ReportsBar, ToolsBar,
  HelpBar, ExpandBar, SearchBar, HelpBarWithoutFeedback, SearchDropdownButton
} from './toolbar-elements';
import { MmcButton } from './mmc-button';
import { MyWebsiteButton } from './my-website-button';
import { AssetLibraryButton } from './asset-library-button';
import { CopilotButton } from './copilot-button';

const renderExpandedToolbar = (
  classes,
  i18n,
  responsiveSize,
  onSearchClick,
  NotificationsBar,
  ReportsV4Bar,
  CustomizedToolsBar,
  hide,
  feedbackProps,
  feedbackEnabled,
  helpProps,
  CustomizedImportBar,
  siteMap,
  renderSearchResults,
  isSearchBarAsDropdownEnabled,
  onCopilotClick,
  serverContext
) => {
  const Tools = CustomizedToolsBar || ToolsBar;
  const Import = CustomizedImportBar || ImportBar;
  return (
    <div className={classNames(classes.container, 'global-menu-toolbar')} >
      {(responsiveSize === 'l' && !hide.searchBar && !isSearchBarAsDropdownEnabled) && (
      <Scenario name="top-nav-persistent-white-box">
        <Activity name="top-nav-persistent-white-box">
          <Col className={classNames([classes.item])}>
            <SearchBar
              onClick={() => onSearchClick()}
              i18n={i18n}
              responsiveSize={responsiveSize}
            />
          </Col>
        </Activity>
      </Scenario>
      )}
      <Col>
        <div className="livechat-text-block ms-support-chat__text-block" hidden />
      </Col>
      {isSearchBarAsDropdownEnabled && !hide.searchBar &&
      <Scenario name="top-nav-dropdown-search-button">
        <Activity name="top-nav-dropdown-search-button">
          <Col className={classNames([classes.item])}>
            <SearchDropdownButton
              i18n={i18n}
              responsiveSize={responsiveSize}
              siteMap={siteMap}
              renderSearchResults={renderSearchResults}
              onCopilotClick={onCopilotClick}
              serverContext={serverContext}
            />
          </Col>
        </Activity>
      </Scenario>
      }
      {!hide.import &&
        <Col className={classNames([classes.item])}>
          <Import
            i18n={i18n}
            responsiveSize={responsiveSize}
          />
        </Col>
      }
      {!hide.myWebsite &&
        <Col className={classNames([classes.item])}>
          <MyWebsiteButton
            i18n={i18n}
            responsiveSize={responsiveSize}
          />
        </Col>
      }
      {hide.mmc === false &&
        <Col className={classNames([classes.item])}>
          <MmcButton
            i18n={i18n}
            responsiveSize={responsiveSize}
          />
        </Col>
      }
      {hide.assetLibrary === false &&
        <Col className={classNames([classes.item])}>
          <AssetLibraryButton
            i18n={i18n}
            responsiveSize={responsiveSize}
          />
        </Col>
      }
      {!hide.reports && ReportsV4Bar &&
        <Col className={classNames([classes.item])}>
          <ReportsV4Bar i18n={i18n} responsiveSize={responsiveSize} />
        </Col>
      }
      {!hide.reports && !ReportsV4Bar &&
        <Col className={classNames([classes.item])}>
          <ReportsBar
            i18n={i18n}
            responsiveSize={responsiveSize}
          />
        </Col>
      }
      {!hide.tools &&
        <Col className={classNames([classes.item])}>
          <Tools
            i18n={i18n}
            responsiveSize={responsiveSize}
          />
        </Col>
      }
      {!(hide.searchBar && hide.import && hide.reports && hide.tools && hide.myWebsite) &&
        <Col>
          <div className={classes.verticalLine} />
        </Col>
      }
      {!hide.helpBar &&
        <Col className={classNames([classes.item])}>
          <Scenario name="top-nav-dropdown-help-button">
            <Activity name="top-nav-dropdown-help-button">
              {
                feedbackEnabled ?
                  <HelpBar
                    i18n={i18n}
                    responsiveSize={responsiveSize}
                    feedbackProps={feedbackProps}
                    helpProps={helpProps}
                  /> :
                  <HelpBarWithoutFeedback onClick={noop} responsiveSize={responsiveSize} text={i18n.getString(_TL_('Help'))} />
              }
            </Activity>
          </Scenario>
        </Col>
      }
      {hide.copilot === false &&
        <Col className={classNames([classes.item])}>
          <CopilotButton
            i18n={i18n}
            onClick={onCopilotClick}
            responsiveSize={responsiveSize}
          />
        </Col>
      }
      {!hide.notificationBar &&
        <Col className={classNames([classes.item])}>
          <NotificationsBar onClick={noop} i18n={i18n} responsiveSize={responsiveSize} />
        </Col>
      }
    </div>
  );
};

const renderCollapsedToolbar = (classes, i18n, responsiveSize, onExpandClick) => (
  <div className={classes.container} >
    <ExpandBar onClick={() => onExpandClick()} i18n={i18n} responsiveSize={responsiveSize} />
  </div>
);

const Toolbar = ({
  classes,
  i18n,
  responsiveSize,
  expanded,
  onExpandClick,
  onSearchClick,
  onCopilotClick,
  NotificationsBar,
  ReportsV4Bar,
  CustomizedToolsBar,
  hide,
  feedbackProps,
  feedbackEnabled,
  helpProps,
  CustomizedImportBar,
  siteMap,
  renderSearchResults,
  isSearchBarAsDropdownEnabled,
  serverContext,
}) => {
  if (responsiveSize !== 'l' || expanded || isSearchBarAsDropdownEnabled) {
    return renderExpandedToolbar(
      classes,
      i18n,
      responsiveSize,
      onSearchClick,
      NotificationsBar,
      ReportsV4Bar,
      CustomizedToolsBar,
      hide,
      feedbackProps,
      feedbackEnabled,
      helpProps,
      CustomizedImportBar,
      siteMap,
      renderSearchResults,
      isSearchBarAsDropdownEnabled,
      onCopilotClick,
      serverContext
    );
  }

  return renderCollapsedToolbar(classes, i18n, responsiveSize, onExpandClick);
};

Toolbar.propTypes = {
  classes: PropTypes.shape(mapObject(
    getToolbarStyles({}, {}),
    constant(PropTypes.string.isRequired)
  )).isRequired,
  i18n: PropTypes.shape({
    getString: PropTypes.func,
  }).isRequired,
  responsiveSize: PropTypes.string.isRequired,
  expanded: PropTypes.bool.isRequired,
  onExpandClick: PropTypes.func.isRequired,
  onSearchClick: PropTypes.func.isRequired,
  onCopilotClick: PropTypes.func.isRequired,
  NotificationsBar: PropTypes.elementType,
  ReportsV4Bar: PropTypes.elementType,
  CustomizedToolsBar: PropTypes.elementType,
  hide: PropTypes.shape({
    import: PropTypes.bool,
    reports: PropTypes.bool,
    tools: PropTypes.bool,
    searchBar: PropTypes.bool,
    assetLibrary: PropTypes.bool,
    copilot: PropTypes.bool,
    helpBar: PropTypes.bool,
    notificationBar: PropTypes.bool,
    myWebsite: PropTypes.bool,
    mmc: PropTypes.bool,
  }).isRequired,
  feedbackProps: PropTypes.shape({
    feedbackLogger: PropTypes.shape({
      log: PropTypes.func,
    }),
    hideFeedback: PropTypes.bool,
    isTestEnv: PropTypes.bool,
    email: PropTypes.string,
    pageId: PropTypes.string,
    sessionId: PropTypes.string,
  }).isRequired,
  helpProps: PropTypes.shape({
    showGetHelp: PropTypes.bool,
    showGetStarted: PropTypes.bool,
    showCommunity: PropTypes.bool,
    customizedMenuItems: PropTypes.arrayOf(PropTypes.shape({
      id: PropTypes.string,
      name: PropTypes.string,
      onClick: PropTypes.func,
      externalLink: PropTypes.string,
      children: PropTypes.array,
      showTag: PropTypes.string,
    })),
  }),
  renderSearchResults: PropTypes.arrayOf(PropTypes.func),
  siteMap: PropTypes.arrayOf(PropTypes.shape({
    name: PropTypes.string,
  })),
  isSearchBarAsDropdownEnabled: PropTypes.bool,
};

Toolbar.defaultProps = {
  isSearchBarAsDropdownEnabled: false,
  renderSearchResults: undefined,
  siteMap: undefined,
};

export const StyledToolbar = withDefaultStyles(Toolbar, getToolbarStyles);
export { getCustomizedToolsBar, smartImportBar } from './toolbar-elements';
export { StyledBaMenuItem } from './menu-item';
export { getButtonClassNames } from './utilities';
export { StyledHeader } from './header';
